import React, { useState } from 'react';
import { useMockData } from './MockDataContext';
import { Power, Plus, FileText, Clock, CheckCircle, Upload, AlertCircle, Lock, Unlock, X, Loader2, UploadCloud, AlertTriangle, Package } from 'lucide-react';

// ============================================================================
// SHARED: Service Configuration Panel (Used in Admin & Client Views)
// ============================================================================
const ServiceConfigPanel = ({ onSelectService }) => {
  const { services, toggleService, addService } = useMockData();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configStep, setConfigStep] = useState(1);
  const [newService, setNewService] = useState({ name: '', state: '', basePrice: 500 });
  const [fixedFields, setFixedFields] = useState({});
  const [defaultValues, setDefaultValues] = useState({});

  const FIELD_DEFS = [
    { id: 'article', label: 'Article', type: 'select', options: ['Affidavit', 'Agreement', 'Indemnity Bond', 'Power of Attorney'] },
    { id: 'denomination', label: 'Denomination (₹)', type: 'number' },
  ];

  const handleToggleLock = (fieldId) => {
    if (fixedFields[fieldId]) {
      const { [fieldId]: _, ...rest } = fixedFields;
      setFixedFields(rest);
      const { [fieldId]: __, ...restVals } = defaultValues;
      setDefaultValues(restVals);
    } else {
      setFixedFields({ ...fixedFields, [fieldId]: true });
    }
  };

  const handleSaveService = () => {
    addService({
      id: `svc_${Date.now()}`,
      ...newService,
      stamps: [{ id: 1, fixedFields, defaultValues }],
      isEnabled: true,
    });
    setShowConfigModal(false);
    setConfigStep(1);
    setNewService({ name: '', state: '', basePrice: 500 });
    setFixedFields({});
    setDefaultValues({});
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <h3 className="font-bold text-gray-800">Live Stamping Services</h3>
        <button 
          onClick={() => setShowConfigModal(true)}
          className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 flex items-center space-x-1"
        >
          <Plus size={14} /> <span>New Service</span>
        </button>
      </div>
      
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map(svc => (
          <div 
            key={svc.id} 
            className={`p-4 rounded-lg border-2 transition-all cursor-pointer group ${svc.isEnabled ? 'border-green-200 bg-green-50/50 hover:border-green-400' : 'border-gray-200 bg-gray-50 opacity-60'}`}
            onClick={() => svc.isEnabled && onSelectService && onSelectService(svc)}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-bold text-gray-800">{svc.name}</h4>
                <p className="text-xs text-gray-500">{svc.state} • ₹{svc.basePrice}</p>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); toggleService(svc.id); }}
                className={`p-1.5 rounded-full ${svc.isEnabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'}`}
              >
                <Power size={14} />
              </button>
            </div>
          </div>
        ))}
        {services.length === 0 && (
          <div className="col-span-full text-center py-8 text-gray-400 italic">No services configured</div>
        )}
      </div>

      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center">
              <h2 className="font-bold text-lg text-gray-800">{configStep === 1 ? 'New Service' : 'Configure Fields'}</h2>
              <button onClick={() => setShowConfigModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {configStep === 1 && (
                <div className="space-y-4 max-w-md mx-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select className="w-full p-2.5 border rounded-lg" value={newService.state} onChange={e => setNewService({...newService, state: e.target.value})}>
                      <option value="">Select...</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Delhi">Delhi</option>
                      <option value="Karnataka">Karnataka</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                    <input className="w-full p-2.5 border rounded-lg" value={newService.name} onChange={e => setNewService({...newService, name: e.target.value})} placeholder="e.g., MH Rental Agreement" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (₹)</label>
                    <input type="number" className="w-full p-2.5 border rounded-lg" value={newService.basePrice} onChange={e => setNewService({...newService, basePrice: Number(e.target.value)})} />
                  </div>
                </div>
              )}
              {configStep === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 mb-4">Lock fields to set constant values. Unlocked fields will be filled by the client.</p>
                  {FIELD_DEFS.map(field => (
                    <div key={field.id} className={`p-4 rounded-lg border ${fixedFields[field.id] ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-dashed border-gray-300'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-700">{field.label}</span>
                        <button onClick={() => handleToggleLock(field.id)} className={`p-1.5 rounded-full ${fixedFields[field.id] ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                          {fixedFields[field.id] ? <Lock size={14} /> : <Unlock size={14} />}
                        </button>
                      </div>
                      {fixedFields[field.id] ? (
                        field.type === 'select' ? (
                          <select className="w-full p-2 border rounded" value={defaultValues[field.id] || ''} onChange={e => setDefaultValues({...defaultValues, [field.id]: e.target.value})}>
                            <option value="">Select constant...</option>
                            {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input type="number" className="w-full p-2 border rounded" placeholder="Set constant value" value={defaultValues[field.id] || ''} onChange={e => setDefaultValues({...defaultValues, [field.id]: e.target.value})} />
                        )
                      ) : <div className="text-sm text-gray-400 italic">Client will provide</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end space-x-3">
              {configStep === 2 && <button onClick={() => setConfigStep(1)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Back</button>}
              <button 
                disabled={configStep === 1 && (!newService.state || !newService.name)}
                onClick={() => configStep === 1 ? setConfigStep(2) : handleSaveService()}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {configStep === 1 ? 'Next: Configure Fields' : 'Create Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CLIENT: Order Request Modal
// ============================================================================
const OrderRequestModal = ({ isOpen, onClose, service }) => {
  const { placeOrder, walletBalance } = useMockData();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !service) return null;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    await new Promise(r => setTimeout(r, 1000));
    const result = placeOrder({
      id: `ord_${Date.now()}`,
      serviceId: service.id,
      serviceName: service.name,
      state: service.state,
      status: 'placed',
      amount: service.basePrice,
      createdAt: new Date().toISOString(),
      details: formData
    });
    if (result.success) {
      onClose();
      setFormData({});
    } else {
      setError(result.message);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="p-5 border-b border-gray-100 flex justify-between items-center">
          <h2 className="font-bold text-lg">{service.name}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Show locked fields as read-only */}
          {service.stamps?.[0] && Object.entries(service.stamps[0].fixedFields).map(([key, isLocked]) => isLocked && (
            <div key={key} className="bg-indigo-50 p-3 rounded-lg">
              <div className="text-xs text-indigo-500 font-bold uppercase">{key.replace('_', ' ')}</div>
              <div className="font-medium text-indigo-900">{service.stamps[0].defaultValues[key]}</div>
            </div>
          ))}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Party Name</label>
            <input className="w-full p-2.5 border rounded-lg" onChange={e => setFormData({...formData, first_party: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Second Party Name</label>
            <input className="w-full p-2.5 border rounded-lg" onChange={e => setFormData({...formData, second_party: e.target.value})} />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
            <span className="text-gray-600">Wallet Balance</span>
            <span className={`font-bold ${walletBalance >= service.basePrice ? 'text-green-600' : 'text-red-600'}`}>₹{walletBalance}</span>
          </div>
          
          {error && <div className="text-red-600 text-sm flex items-center"><AlertCircle size={14} className="mr-1" />{error}</div>}

          <button 
            onClick={handleSubmit}
            disabled={isSubmitting || walletBalance < service.basePrice || !formData.first_party}
            className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
          >
            {isSubmitting ? <Loader2 className="animate-spin" /> : `Pay ₹${service.basePrice} & Submit`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// VENDOR: Upload & OCR Modal
// ============================================================================
const VendorUploadModal = ({ isOpen, onClose, order }) => {
  const { updateOrderStatus } = useMockData();
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [outcome, setOutcome] = useState(null);

  if (!isOpen || !order) return null;

  const startSimulation = () => {
    setPhase('uploading');
    let p = 0;
    const uploadInterval = setInterval(() => {
      p += 5;
      if (p >= 50) {
        clearInterval(uploadInterval);
        setPhase('ocr');
        let ocrP = 50;
        const ocrInterval = setInterval(() => {
          ocrP += 2;
          if (ocrP >= 100) {
            clearInterval(ocrInterval);
            const success = Math.random() > 0.3;
            setOutcome(success ? 'success' : 'review');
            setPhase('outcome');
            updateOrderStatus(order.id, success ? 'completed' : 'ops_review');
          } else {
            setProgress(ocrP);
          }
        }, 50);
      } else {
        setProgress(p);
      }
    }, 80);
  };

  const handleClose = () => {
    setPhase('idle');
    setProgress(0);
    setOutcome(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-gray-800">Upload Document</h3>
          <button onClick={handleClose}><X size={20} className="text-gray-400" /></button>
        </div>

        {phase === 'idle' && (
          <div onClick={startSimulation} className="border-2 border-dashed border-indigo-200 bg-indigo-50/50 rounded-xl p-10 flex flex-col items-center cursor-pointer hover:bg-indigo-50 transition-colors">
            <UploadCloud className="text-indigo-500 mb-2" size={40} />
            <p className="font-medium text-indigo-900">Click to Simulate Upload</p>
          </div>
        )}

        {(phase === 'uploading' || phase === 'ocr') && (
          <div className="py-8 text-center">
            <div className="mb-4 text-gray-800 font-bold">{phase === 'uploading' ? 'Uploading...' : 'Verifying (OCR)...'}</div>
            <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden mb-2">
              <div className={`h-full rounded-full ${phase === 'ocr' ? 'bg-purple-500' : 'bg-indigo-500'}`} style={{ width: `${progress}%`, transition: 'width 0.1s' }} />
            </div>
            <div className="text-xs text-gray-400">{progress}%</div>
          </div>
        )}

        {phase === 'outcome' && (
          <div className="text-center py-6">
            {outcome === 'success' ? (
              <>
                <CheckCircle className="mx-auto text-green-600 mb-4" size={48} />
                <h3 className="text-xl font-bold text-green-700">Verified!</h3>
                <p className="text-gray-500 mt-2">Order marked as Completed.</p>
              </>
            ) : (
              <>
                <AlertTriangle className="mx-auto text-orange-600 mb-4" size={48} />
                <h3 className="text-xl font-bold text-orange-700">Flagged for Review</h3>
                <p className="text-gray-500 mt-2">Sent to Ops for manual check.</p>
              </>
            )}
            <button onClick={handleClose} className="mt-6 w-full bg-gray-900 text-white py-2.5 rounded-lg font-medium">Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN: Unified Orders Page (Tabbed: Bulk | Live Stamping)
// ============================================================================
const MockOrdersPage = () => {
  const { currentUserRole, orders, walletBalance, addFunds } = useMockData();
  const [activeTab, setActiveTab] = useState('live'); // Default to Live Stamping
  const [selectedService, setSelectedService] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const liveOrders = orders; // In mock, all orders are "live stamping" type

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with Wallet (for Client) */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500">Manage bulk and live stamping orders</p>
        </div>
        
        {currentUserRole === 'client' && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg">
            <div className="text-xs text-indigo-200">Wallet</div>
            <div className="text-xl font-bold">₹{walletBalance.toLocaleString()}</div>
            <button onClick={() => addFunds(500)} className="mt-1 text-xs underline opacity-80 hover:opacity-100">+ Add ₹500</button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        <button 
          onClick={() => setActiveTab('bulk')}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'bulk' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Bulk Orders
        </button>
        <button 
          onClick={() => setActiveTab('live')}
          className={`px-6 py-2.5 rounded-lg text-sm font-bold flex items-center space-x-2 transition-all relative ${activeTab === 'live' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <span>Live Stamping</span>
          <span className="relative flex h-2 w-2 ml-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
          </span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'bulk' && (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-400 italic">
          Bulk Orders UI (Not part of this prototype)
        </div>
      )}

      {activeTab === 'live' && (
        <div>
          {/* Service Config Panel (Admin can configure, Client can select) */}
          {(currentUserRole === 'admin' || currentUserRole === 'client') && (
            <ServiceConfigPanel onSelectService={currentUserRole === 'client' ? setSelectedService : null} />
          )}

          {/* Order List */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-bold text-gray-800">
                {currentUserRole === 'vendor' ? 'Pending Requests' : 'Your Live Stamp Orders'}
              </h3>
              <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
                {liveOrders.filter(o => o.status === 'placed').length} Active
              </span>
            </div>

            {liveOrders.length === 0 ? (
              <div className="p-12 text-center text-gray-400 italic">
                {currentUserRole === 'client' ? 'Click on an active service above to create an order.' : 'No pending orders.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {liveOrders.map(order => (
                  <div key={order.id} className="p-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                        <FileText size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">{order.serviceName}</h4>
                        <p className="text-sm text-gray-500">{order.details?.first_party} / {order.details?.second_party}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                        order.status === 'placed' ? 'bg-orange-100 text-orange-600' :
                        order.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {order.status}
                      </div>
                      {currentUserRole === 'vendor' && order.status === 'placed' && (
                        <button 
                          onClick={() => setSelectedOrder(order)}
                          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center space-x-1"
                        >
                          <Upload size={14} />
                          <span>Upload</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <OrderRequestModal isOpen={!!selectedService} onClose={() => setSelectedService(null)} service={selectedService} />
      <VendorUploadModal isOpen={!!selectedOrder} onClose={() => setSelectedOrder(null)} order={selectedOrder} />
    </div>
  );
};

export default MockOrdersPage;
